import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Dropdown,
  DropdownMenu,
  DropdownItem,
  DropdownDivider,
} from 'semantic-ui-react';
import './NestedMultiselectValue.css';

/**
 * NestedMultiselectValue — a two-level multiselect built as nested Semantic UI
 * `Dropdown`s: the top level lists the groups, each group flies its names out
 * in a pointing submenu.
 *
 * Selection is moved out of the menu rather than marked inside it: picking a
 * name removes it from the list and promotes it to a removable chip in the
 * field (a group with nothing left disappears), removing the chip puts it back.
 * The chips are Semantic's own — it takes the menu from `children` but still
 * derives labels from `options`/`value`, so removal goes through `onChange`.
 *
 * Input contract: `rule.value` is a flat list [{ id, groupName, name, checked }].
 * Level 1 is the distinct `groupName`s, level 2 the `name`s within a group;
 * the whole list is persisted back, so unselected items and the grouping
 * survive. The contract is deliberately domain-agnostic (never
 * partner/channel): a rule maps its own payload onto it via
 * `mapToComponentFromStore`/`mapToStoreFromComponent` in FIELDS_CONFIG.
 *
 * onValueChange follows the 2-arg multiselect contract:
 *   onValueChange(instanceId, jsonString)
 */

// Parse the persisted value into a flat item array: accepts an already-parsed
// array or a JSON string, and never throws on bad/empty values.
const parseItems = val => {
  if (Array.isArray(val)) return val;
  if (typeof val === 'string' && val.trim() !== '') {
    try {
      const parsed = JSON.parse(val);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }
  return [];
};

// Group a flat item list by `groupName`, preserving first-seen order.
const groupByName = items => {
  const groups = new Map();
  items.forEach(item => {
    if (!groups.has(item.groupName)) groups.set(item.groupName, []);
    groups.get(item.groupName).push(item);
  });
  return groups;
};

// The flat catalog as Semantic options. They never reach the menu (it is built
// from children) — Semantic only uses them to turn `value` into chips, so
// `groupName` rides along for the chip layout.
const toOptions = items => items.map(item => ({
  key: item.id,
  value: item.id,
  text: item.name,
  groupName: item.groupName,
}));

const NestedMultiselectValue = ({ rule, onValueChange }) => {
  // Only one group may show its fly-out at a time: the submenu follows the
  // pointer, so moving onto another group row hides the previous level 2.
  const [openGroup, setOpenGroup] = useState(null);
  const items = parseItems(rule.value);
  const selectedIds = items.filter(item => item.checked).map(item => item.id);
  // Only the unselected items feed the menu — a picked name lives in the chips.
  const availableItems = items.filter(item => !item.checked);
  const groups = groupByName(availableItems);
  const groupNames = [...groups.keys()];

  const persist = next => onValueChange(rule.instanceId, JSON.stringify(next));

  // Flip one item's `checked` and persist the whole list, grouping included.
  const setChecked = (id, checked) => {
    persist(items.map(item => (item.id === id ? { ...item, checked } : item)));
  };

  // Semantic drives this only when a chip is removed (menu clicks are handled
  // by the nested items above): sync `checked` with the ids it hands back.
  const handleChange = (e, data) => {
    const nextIds = new Set(data.value);
    persist(items.map(item => ({ ...item, checked: nextIds.has(item.id) })));
  };

  // No catalog at all / catalog fully consumed / names still available.
  const hasItems = items.length > 0;
  let placeholder = 'Select options...';
  if (!hasItems) placeholder = 'No options available';
  else if (!availableItems.length) placeholder = 'All options selected';

  return (
    <Dropdown
      multiple
      selection
      fluid
      disabled={!hasItems}
      className='nested-multiselect'
      options={toOptions(items)}
      value={selectedIds}
      onChange={handleChange}
      onClose={() => setOpenGroup(null)}
      placeholder={placeholder}
      data-testid={`nested-multiselect-${rule.id}`}
      icon={(
        <i
          className='dropdown icon'
          data-testid={`nested-multiselect-toggle-${rule.id}`}
        />
      )}
      renderLabel={item => ({
        className: 'nested-multiselect__chip',
        title: `${item.groupName} — ${item.text}`,
        'data-testid': `nested-multiselect-chip-${rule.id}-${item.value}`,
        // The delete icon is a small hit target — a click anywhere on the
        // chip removes it, overriding Semantic's default (marks it "active").
        onClick: e => {
          e.stopPropagation();
          setChecked(item.value, false);
        },
        content: (
          <>
            <span className='nested-multiselect__chip-group'>{item.groupName}</span>
            <span className='nested-multiselect__chip-name'>{item.text}</span>
          </>
        ),
        removeIcon: (
          <i
            className='delete icon'
            data-testid={`nested-multiselect-chip-delete-${rule.id}-${item.value}`}
          />
        ),
      })}
    >
      <DropdownMenu>
        {groupNames.length === 0 ? (
          <DropdownItem
            disabled
            text={placeholder}
            data-testid={`nested-multiselect-empty-${rule.id}`}
          />
        ) : (
          groupNames.map((groupName, groupIndex) => {
            const groupItems = groups.get(groupName) || [];

            return (
              <React.Fragment key={groupName}>
                {groupIndex > 0 && <DropdownDivider />}
                {/* Level 1 — a group is itself a dropdown that flies its names out. */}
                <Dropdown
                  item
                  pointing='left'
                  className='nested-multiselect__group'
                  open={openGroup === groupName}
                  onMouseEnter={() => setOpenGroup(groupName)}
                  onMouseLeave={() => setOpenGroup(
                    current => (current === groupName ? null : current)
                  )}
                  data-testid={`nested-multiselect-group-${rule.id}-${groupName}`}
                  trigger={(
                    <span className='nested-multiselect__group-trigger'>
                      <span className='nested-multiselect__group-name'>{groupName}</span>
                    </span>
                  )}
                >
                  <DropdownMenu>
                    {groupItems.map(item => (
                      // Level 2 — a selectable name. stopPropagation keeps the parent
                      // and group menus open while picking (multiselect behaviour).
                      <DropdownItem
                        key={item.id}
                        className='nested-multiselect__option'
                        onClick={e => {
                          e.stopPropagation();
                          setChecked(item.id, true);
                        }}
                        data-testid={`nested-multiselect-option-${rule.id}-${groupName}-${item.id}`}
                      >
                        {item.name}
                      </DropdownItem>
                    ))}
                  </DropdownMenu>
                </Dropdown>
              </React.Fragment>
            );
          })
        )}
      </DropdownMenu>
    </Dropdown>
  );
};

NestedMultiselectValue.propTypes = {
  rule: PropTypes.object.isRequired,
  onValueChange: PropTypes.func.isRequired,
};

export default NestedMultiselectValue;
